import { useState, useEffect, useRef } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { WikiArticle } from '@/context/WikiContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';

interface Props {
  article: WikiArticle;
}

function AnimatedBar({ value, color, delay }: { value: number; color: string; delay: number }) {
  const [width, setWidth] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setWidth(0);
    const timer = setTimeout(() => setWidth(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return (
    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-1000 ease-out"
        style={{ width: `${width}%`, background: color }}
        ref={ref}
      />
    </div>
  );
}

function CountUp({ target, suffix = '', duration = 1500 }: { target: number; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    setCount(0);
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const interval = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(interval);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(interval);
  }, [target, duration]);

  return <>{count}{suffix}</>;
}

export default function WikiArticleView({ article }: Props) {
  const { theme } = useTheme();
  const { settings } = useBadgeVisibility();
  const light = theme === 'light';
  const [activeTab, setActiveTab] = useState<'overview' | 'specs' | 'compare'>('overview');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(false);
    setActiveTab('overview');
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, [article.id]);

  const perf = article.performance;

  const card = light ? 'bg-white border border-gray-100' : 'bg-[#1C2B3A] border border-white/5';
  const text = light ? 'text-gray-900' : 'text-white';
  const muted = light ? 'text-gray-500' : 'text-white/50';
  const subBg = light ? 'bg-gray-50' : 'bg-[#0D1B2A]';

  return (
    <div
      className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
    >
      {/* Hero image + title */}
      <div className="relative w-full h-64 md:h-80 rounded-2xl overflow-hidden mb-8">
        <img
          src={article.heroImage}
          alt={article.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 p-8">
          {settings.wikiBadges && (
            <span
              className="inline-block text-xs font-black tracking-widest uppercase px-3 py-1 rounded-full mb-3 text-white"
              style={{ background: article.badgeColor }}
            >
              {article.badge}
            </span>
          )}
          <h2 className="text-3xl md:text-4xl font-black text-white">{article.title}</h2>
          <p className="text-white/70 text-sm mt-1">{article.subtitle}</p>
        </div>
      </div>

      {/* Inner tabs */}
      <div className={`flex gap-1 p-1 rounded-xl mb-6 ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
        {(['overview', 'specs', 'compare'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`flex-1 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all cursor-pointer whitespace-nowrap ${
              activeTab === t
                ? 'text-white'
                : light ? 'text-gray-500 hover:text-gray-800' : 'text-white/40 hover:text-white/70'
            }`}
            style={activeTab === t ? { background: article.badgeColor } : {}}
          >
            {t === 'overview' ? 'Overview' : t === 'specs' ? 'Specs' : 'Performance'}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Summary */}
          <div className={`${card} rounded-xl p-6`}>
            <p className={`text-base leading-relaxed font-medium ${text}`}>{article.summary}</p>
          </div>

          {/* Body text */}
          <div className={`${card} rounded-xl p-6`}>
            <h3 className={`font-black text-sm uppercase tracking-widest mb-4`} style={{ color: article.badgeColor }}>
              <i className="ri-book-open-line mr-2"></i>Deep Dive
            </h3>
            {article.body.split('\n\n').map((para, i) => (
              <p key={i} className={`${muted} text-sm leading-relaxed mb-4 last:mb-0`}>{para}</p>
            ))}
          </div>

          {/* Pros & Cons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`${card} rounded-xl p-6`}>
              <h3 className="font-black text-sm uppercase tracking-widest text-emerald-400 mb-4">
                <i className="ri-thumb-up-line mr-2"></i>Advantages
              </h3>
              <ul className="space-y-2.5">
                {article.pros.map((p, i) => (
                  <li
                    key={i}
                    className={`flex items-start gap-3 text-sm ${muted} transition-all duration-300`}
                    style={{ transitionDelay: `${i * 60}ms`, opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(-10px)' }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-500/20 shrink-0 mt-0.5">
                      <i className="ri-check-line text-emerald-400 text-xs"></i>
                    </div>
                    {p.text}
                  </li>
                ))}
              </ul>
            </div>
            <div className={`${card} rounded-xl p-6`}>
              <h3 className="font-black text-sm uppercase tracking-widest text-red-400 mb-4">
                <i className="ri-thumb-down-line mr-2"></i>Limitations
              </h3>
              <ul className="space-y-2.5">
                {article.cons.map((c, i) => (
                  <li
                    key={i}
                    className={`flex items-start gap-3 text-sm ${muted} transition-all duration-300`}
                    style={{ transitionDelay: `${i * 60}ms`, opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(10px)' }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-red-500/20 shrink-0 mt-0.5">
                      <i className="ri-close-line text-red-400 text-xs"></i>
                    </div>
                    {c.text}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Use cases */}
          <div className={`${card} rounded-xl p-6`}>
            <h3 className="font-black text-sm uppercase tracking-widest mb-4" style={{ color: article.badgeColor }}>
              <i className="ri-anchor-line mr-2"></i>Best For
            </h3>
            <div className="flex flex-wrap gap-2">
              {article.useCases.map((uc, i) => (
                <span
                  key={i}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full text-white"
                  style={{ background: `${article.badgeColor}25`, border: `1px solid ${article.badgeColor}50`, color: article.badgeColor }}
                >
                  {uc}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Specs */}
      {activeTab === 'specs' && (
        <div className="space-y-4">
          <div className={`${card} rounded-xl p-6`}>
            <h3 className="font-black text-sm uppercase tracking-widest mb-5" style={{ color: article.badgeColor }}>
              <i className="ri-list-check-2 mr-2"></i>Technical Specifications
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {article.specs.map((spec, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between p-4 rounded-xl transition-all duration-300 ${subBg}`}
                  style={{ transitionDelay: `${i * 80}ms`, opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(12px)' }}
                >
                  <span className={`text-xs font-semibold uppercase tracking-wide ${muted}`}>{spec.label}</span>
                  <span className={`text-sm font-black ${text}`} style={{ color: article.badgeColor }}>{spec.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Performance compare */}
      {activeTab === 'compare' && (
        <div className={`${card} rounded-xl p-6 space-y-6`}>
          <h3 className="font-black text-sm uppercase tracking-widest mb-2" style={{ color: article.badgeColor }}>
            <i className="ri-bar-chart-grouped-line mr-2"></i>Performance Ratings
          </h3>
          <p className={`text-xs ${muted} -mt-4`}>Relative performance scores for {article.title} technology</p>

          {[
            { label: 'Cycle Life', value: perf.cycleLife, icon: 'ri-refresh-line' },
            { label: 'Weight Efficiency', value: perf.weight, icon: 'ri-scales-line' },
            { label: 'Voltage Stability', value: perf.voltage, icon: 'ri-pulse-line' },
            { label: 'Cost Efficiency', value: perf.cost, icon: 'ri-money-dollar-circle-line' },
          ].map((metric, i) => (
            <div key={i}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${metric.icon} text-sm`} style={{ color: article.badgeColor }}></i>
                  </div>
                  <span className={`text-sm font-semibold ${text}`}>{metric.label}</span>
                </div>
                <span className="text-sm font-black" style={{ color: article.badgeColor }}>
                  <CountUp target={metric.value} suffix="/100" />
                </span>
              </div>
              <AnimatedBar value={metric.value} color={article.badgeColor} delay={i * 150} />
            </div>
          ))}

          {/* Animated stat cards */}
          <div className="grid grid-cols-3 gap-3 pt-2">
            {article.specs.slice(0, 3).map((spec, i) => (
              <div
                key={i}
                className={`${subBg} rounded-xl p-4 text-center transition-all duration-500`}
                style={{ transitionDelay: `${i * 100}ms`, opacity: visible ? 1 : 0, transform: visible ? 'scale(1)' : 'scale(0.9)' }}
              >
                <p className="text-lg font-black" style={{ color: article.badgeColor }}>{spec.value}</p>
                <p className={`text-xs ${muted} mt-1`}>{spec.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
